import React from 'react';
import { DeleteOption } from '../DeleteOption';
import { DeleteHandler } from '../DeleteOption/DeleteModal';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { DeleteNodeOption_node$key } from './__generated__/DeleteNodeOption_node.graphql';
import { DeleteNodeOption_deleteFolderNodes_Mutation } from './__generated__/DeleteNodeOption_deleteFolderNodes_Mutation.graphql';
import { t, Trans } from '@lingui/macro';
import { DeleteNodeOption_deleteEvent_Mutation } from './__generated__/DeleteNodeOption_deleteEvent_Mutation.graphql';
import { chakra, Text, useToast } from '@cardboard-ui/react';
import {
  generatePath,
  matchPath,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { DeleteNodeOption_deleteAnnouncement_Mutation } from './__generated__/DeleteNodeOption_deleteAnnouncement_Mutation.graphql';
import { DeleteNodeOption_deleteVaultItems_Mutation } from './__generated__/DeleteNodeOption_deleteVaultItems_Mutation.graphql';
import {
  CALENDAR_PATH,
  EVENT_PATH,
  SPACE_EVENT_PATH,
  SPACE_EVENTS_PATH,
} from 'utils/routes';
import { useFeatureFlag } from 'utils/sessionProvider';

const nodeFragment = graphql`
  fragment DeleteNodeOption_node on Node {
    __typename
    id
    ... on FolderNodeInterface {
      canDelete {
        value
      }
      name
    }
    ... on HasOwnerPermissionsInterface {
      ownershipEvent {
        id
      }
    }
    ... on Event {
      name
      canDelete {
        value
      }
      space {
        id
      }
    }
    ... on Announcement {
      canDelete {
        value
      }
    }
    ... on VaultItem {
      canDelete {
        value
      }
    }
  }
`;

const deleteFolderNodeMutation = graphql`
  mutation DeleteNodeOption_deleteFolderNodes_Mutation(
    $folderNodeIds: [ID!]!
    $connections: [ID!]!
  ) {
    deleteFolderNodes(input: { folderNodeIds: $folderNodeIds }) {
      folderNodeDeletedIds @deleteEdge(connections: $connections)
      folderNodesDeleted {
        __typename
        ... on Folder {
          id
          publishState
        }
        ... on FolderItem {
          id
          publishState
        }
      }
    }
  }
`;

const deleteEventMutation = graphql`
  mutation DeleteNodeOption_deleteEvent_Mutation($eventId: ID!) {
    deleteEvent(input: { id: $eventId }) {
      eventDeleted {
        publishState
      }
    }
  }
`;

const deleteAnnouncementMutation = graphql`
  mutation DeleteNodeOption_deleteAnnouncement_Mutation(
    $id: ID!
    $connections: [ID!]!
  ) {
    deleteAnnouncement(input: { id: $id }) {
      announcementDeletedId @deleteEdge(connections: $connections)
    }
  }
`;

const deleteVaultItemMutation = graphql`
  mutation DeleteNodeOption_deleteVaultItems_Mutation(
    $vaultItemIds: [ID!]!
    $connections: [ID!]!
  ) {
    deleteVaultItems(input: { vaultItemIds: $vaultItemIds }) {
      vaultItemDeletedIds @deleteEdge(connections: $connections)
    }
  }
`;

interface DeleteNodeOptionProps {
  node: DeleteNodeOption_node$key;
  connectionId?: string;
  onCompleted?: () => void;
  hidden?: boolean;
}

interface useIsViewingEventProps {
  eventId: string;
  spaceId: string | undefined;
}

interface useIsViewingEventReturn {
  isViewingEvent: boolean;
  returnPathAfterDeletingEvent?: string;
}

function useIsViewingEvent({
  eventId,
  spaceId,
}: useIsViewingEventProps): useIsViewingEventReturn {
  const path = useLocation().pathname;

  if (!spaceId) return { isViewingEvent: false };

  const fullPathInSpace = generatePath(SPACE_EVENT_PATH, { eventId, spaceId });
  const fullPathInCealendar = generatePath(EVENT_PATH, { eventId });
  const isViewingEventInCalendar = !!matchPath(
    { path: fullPathInCealendar },
    path,
  );
  const isViewingEventInSpace = !!matchPath({ path: fullPathInSpace }, path);
  return {
    isViewingEvent: isViewingEventInCalendar || isViewingEventInSpace,
    returnPathAfterDeletingEvent: isViewingEventInSpace
      ? generatePath(SPACE_EVENTS_PATH, { spaceId })
      : CALENDAR_PATH,
  };
}

const useCustomTitleAndBody = (node: DeleteNodeOption_node$key) => {
  const { __typename, name, ownershipEvent } = useFragment(nodeFragment, node);

  switch (__typename) {
    case 'Folder':
      if (ownershipEvent) {
        return {
          customTitle: t`Delete Folder?`,
          customBody: (
            <Trans>
              <Text>
                Are you sure you want to delete the folder{' '}
                <chakra.span fontWeight="bold">{name}</chakra.span> from the
                event pack? All files and sub-folders inside this folder will
                also be deleted.
              </Text>
            </Trans>
          ),
        };
      } else {
        return {
          customTitle: t`Delete Folder?`,
          customBody: (
            <Trans>
              <Text>
                Are you sure you want to delete the folder{' '}
                <chakra.span fontWeight="bold">{name}</chakra.span> and all its
                contents, including files and sub-folders?
              </Text>
            </Trans>
          ),
        };
      }
    case 'FolderItem':
      if (ownershipEvent) {
        return {
          customTitle: t`Delete File?`,
          customBody: (
            <Trans>
              <Text>
                Are you sure you want to delete{' '}
                <chakra.span fontWeight="bold">{name}</chakra.span> from the
                event pack?
              </Text>
            </Trans>
          ),
        };
      } else {
        return {
          customTitle: t`Delete File?`,
          customBody: (
            <Trans>
              <Text>
                Are you sure you want to delete{' '}
                <chakra.span fontWeight="bold">{name}</chakra.span>?
              </Text>
            </Trans>
          ),
        };
      }

    case 'Event':
      return {
        customTitle: t`Delete Event?`,
        customBody: (
          <Trans>
            <Text>
              Are you sure you want to delete the event{' '}
              <chakra.span fontWeight="bold">{name}</chakra.span>? This will
              also delete all files in the associated event pack.
            </Text>
          </Trans>
        ),
      };

    case 'VaultItem':
      return {
        customTitle: t`Delete File?`,
        customBody: (
          <Trans>
            <Text>
              Are you sure you want to delete{' '}
              <chakra.span fontWeight="bold">{name}</chakra.span>?{` `}
              This file will no longer be accessible to any members of the
              Vault.
            </Text>
          </Trans>
        ),
      };
    case 'Announcement':
      return {
        customTitle: t`Delete announcement`,
        customBody: (
          <Text>{t`Are you sure you want to delete this announcement? All attached files, comments, and reactions will also be removed.`}</Text>
        ),
      };
    default:
      return {
        customTitle: undefined,
        customBody: undefined,
      };
  }
};

export const DeleteNodeOption = ({
  node: _node,
  connectionId,
  hidden,
  onCompleted,
}: DeleteNodeOptionProps) => {
  const { __typename, id, name, canDelete, ownershipEvent, space } =
    useFragment(nodeFragment, _node);

  const toast = useToast();
  const navigate = useNavigate();

  const { customTitle, customBody } = useCustomTitleAndBody(_node);

  const { isViewingEvent, returnPathAfterDeletingEvent } = useIsViewingEvent({
    eventId: id,
    spaceId: space?.id,
  });

  const [deleteFolderNode, isDeleteFolderNodeLoading] =
    useMutation<DeleteNodeOption_deleteFolderNodes_Mutation>(
      deleteFolderNodeMutation,
    );

  const [deleteEvent, isDeleteEventLoading] =
    useMutation<DeleteNodeOption_deleteEvent_Mutation>(deleteEventMutation);

  const [deleteAnnouncement, isDeleteAnnouncementLoading] =
    useMutation<DeleteNodeOption_deleteAnnouncement_Mutation>(
      deleteAnnouncementMutation,
    );

  const [deleteVaultItems, isDeleteVaultItemsLoading] =
    useMutation<DeleteNodeOption_deleteVaultItems_Mutation>(
      deleteVaultItemMutation,
    );

  const customDeletionTextEnabled = useFeatureFlag(
    'FEATURE_CONTEXT_AWARE_DELETION_MODALS',
  );

  if (hidden || !canDelete?.value) return null;

  const onFolderNodeDelete = (onCompletedInner: () => void) => {
    deleteFolderNode({
      variables: {
        folderNodeIds: [id],
        connections: connectionId ? [connectionId] : [],
      },
      updater: (store) => {
        if (ownershipEvent) {
          const eventRecord = store.get(ownershipEvent.id);
          eventRecord?.invalidateRecord();
        }
      },
      onCompleted: onCompleted ?? (() => {}),
    });
  };

  const onEventDelete = (onCompletedInner: () => void) => {
    deleteEvent({
      variables: { eventId: id },
      onCompleted: () => {
        toast({
          status: 'success',
          title: t`Event deleted`,
          description: t`The "${name}" event and its content were erased`,
        });
        if (isViewingEvent && returnPathAfterDeletingEvent) {
          navigate(returnPathAfterDeletingEvent);
        }

        onCompleted?.();
        onCompletedInner();
      },
    });
  };

  const onAnnouncementDelete = (onCompletedInner: () => void) => {
    deleteAnnouncement({
      variables: {
        id,
        connections: connectionId ? [connectionId] : [],
      },
      onCompleted: onCompletedInner,
    });
  };

  const onDeleteVaultItems = (onCompletedInner: () => void) => {
    deleteVaultItems({
      variables: {
        vaultItemIds: [id],
        connections: connectionId ? [connectionId] : [],
      },
      onCompleted: onCompleted ?? (() => {}),
    });
  };

  const handleDelete: DeleteHandler = (onCompletedInner: () => void) => {
    switch (__typename) {
      case 'Folder':
      case 'FolderItem':
        onFolderNodeDelete(onCompletedInner);
        break;
      case 'Event':
        onEventDelete(onCompletedInner);
        break;
      case 'VaultItem':
        onDeleteVaultItems(onCompletedInner);
        break;
      case 'Announcement':
        onAnnouncementDelete(onCompletedInner);
        break;
    }
  };

  const isDeleteLoading =
    isDeleteFolderNodeLoading ||
    isDeleteEventLoading ||
    isDeleteAnnouncementLoading ||
    isDeleteVaultItemsLoading;

  return (
    <DeleteOption
      isDeleting={isDeleteLoading}
      onDelete={handleDelete}
      customTitle={customDeletionTextEnabled ? customTitle : undefined}
      customBody={customDeletionTextEnabled ? customBody : undefined}
      customConfirm={customDeletionTextEnabled ? t`Delete` : undefined}
    />
  );
};
